import React, { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDarkMode } from "../../App";
import AppHeader from "../../components/AppHeader";
import { useGuestAuth } from "../../context/GuestAuthContext";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import SecurityIcon from "@mui/icons-material/Security";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import SearchIcon from "@mui/icons-material/Search";
import HandshakeIcon from "@mui/icons-material/Handshake";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import PaymentsIcon from "@mui/icons-material/Payments";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import homeStyles from "../HomePage.module.css";
import styles from "./AboutPage.module.css";

const FAQS: { q: string; a: string }[] = [
  {
    q: "Is it really free to list a car?",
    a: "Yes. Listing your vehicle is 100% free — no upfront fees, no commissions on the sale, and no hidden charges. You keep every dollar your car sells for.",
  },
  {
    q: "How do you verify sellers?",
    a: "Every seller confirms their email before their listing goes live, and we monitor listings for unusual activity. You can always message privately before committing to anything.",
  },
  {
    q: "How does payment work?",
    a: "Payment happens directly between buyer and seller — we never take a cut. We recommend following our Safety Tips: meet in person, inspect the vehicle, and use a cashier's check or escrow service for high-value sales.",
  },
  {
    q: "Can I message a seller without signing up?",
    a: "To keep the marketplace safe and spam-free, you need a free account to message sellers. It takes under a minute to sign up.",
  },
  {
    q: "Is my personal information protected?",
    a: "Your email is never shared publicly. Buyers and sellers communicate through our in-app messaging until you choose to share contact info.",
  },
];

const AboutPage: React.FC = () => {
  const { darkMode } = useDarkMode();
  const navigate = useNavigate();
  const location = useLocation();
  const { isGuest, openSignIn, openSignUp } = useGuestAuth();

  const goBrowse = () => navigate("/");
  const goSell = () => {
    if (isGuest) openSignUp();
    else navigate("/newlisting");
  };

  // Honor footer / deep-link hashes like /about#faq after client navigation.
  useEffect(() => {
    const hash = location.hash?.replace(/^#/, "");
    if (!hash) return;
    const el = document.getElementById(hash);
    if (!el) return;
    // Defer until layout paints so sticky header offset is accurate.
    const id = window.requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(id);
  }, [location.hash, location.pathname]);

  return (
    <div className={`${homeStyles.pageContainer} ${darkMode ? homeStyles.dark : ""}`}>
      <AppHeader />

      <main className={styles.aboutMain}>
        <section className={styles.aboutHero}>
          <div className={styles.aboutHeroInner}>
            <span className={styles.sectionKicker}>
              <DirectionsCarIcon aria-hidden />
              About Drive Now!
            </span>
            <h1>
              The direct car marketplace.
              <span> No dealership runaround.</span>
            </h1>
            <p>
              Drive Now! connects private buyers and sellers so you can find the
              right car, have a real conversation, and close the deal on your
              terms — without listing fees or platform commissions.
            </p>
            <div className={styles.heroCtas}>
              <button type="button" className={styles.heroCtaPrimary} onClick={goBrowse}>
                Browse listings
                <ArrowForwardIcon aria-hidden />
              </button>
              {isGuest ? (
                <button type="button" className={styles.aboutSecondaryCta} onClick={openSignIn}>
                  Sign In
                </button>
              ) : (
                <Link to="/newlisting" className={styles.aboutSecondaryCta}>
                  Sell a car
                </Link>
              )}
            </div>
          </div>
        </section>

        <section className={styles.valueRail} aria-label="Why DriveNow">
          <div className={styles.valueRailInner}>
            <div>
              <VerifiedUserIcon aria-hidden />
              <span>
                <strong>Account-level trust</strong>Verified contact details
              </span>
            </div>
            <div>
              <SecurityIcon aria-hidden />
              <span>
                <strong>Private by default</strong>Contact details stay private
              </span>
            </div>
            <div>
              <PaymentsIcon aria-hidden />
              <span>
                <strong>$0 listing fees</strong>Keep more of your sale
              </span>
            </div>
            <div>
              <SupportAgentIcon aria-hidden />
              <span>
                <strong>People-first support</strong>Help when you need it
              </span>
            </div>
          </div>
        </section>

        <section className={styles.whySection}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionKicker}>One platform, both sides</span>
            <h2>Designed around the way people actually buy and sell.</h2>
            <p>Less noise, fewer fees, and a clear path to the right conversation.</p>
          </div>
          <div className={styles.whyGrid}>
            <div className={styles.whyCard}>
              <div className={styles.whyCardTop}>
                <span className={styles.whyIconWrap}>
                  <DirectionsCarIcon />
                </span>
                <span className={styles.whyCardLabel}>For buyers</span>
              </div>
              <h3>Find a car, not a sales pitch.</h3>
              <p className={styles.whyCardIntro}>
                Search local inventory and speak directly with the person who
                knows the car.
              </p>
              <ul>
                <li>
                  <VerifiedUserIcon />
                  <span>Seller contact details are verified before messaging</span>
                </li>
                <li>
                  <SearchIcon />
                  <span>Search by make, model, location, price, and mileage</span>
                </li>
                <li>
                  <SecurityIcon />
                  <span>Keep early conversations private and on-platform</span>
                </li>
              </ul>
              <button type="button" className={styles.whyCta} onClick={goBrowse}>
                Explore the marketplace <ArrowForwardIcon />
              </button>
            </div>

            <div className={`${styles.whyCard} ${styles.whyCardSeller}`}>
              <div className={styles.whyCardTop}>
                <span className={styles.whyIconWrap}>
                  <LocalOfferIcon />
                </span>
                <span className={styles.whyCardLabel}>For sellers</span>
              </div>
              <h3>Sell the car. Keep the value.</h3>
              <p className={styles.whyCardIntro}>
                Create a polished listing and connect with interested local
                buyers directly.
              </p>
              <ul>
                <li>
                  <PaymentsIcon />
                  <span>Zero listing fees and no platform commission</span>
                </li>
                <li>
                  <HandshakeIcon />
                  <span>Manage buyer questions in one private inbox</span>
                </li>
                <li>
                  <LocalOfferIcon />
                  <span>Control your price, your schedule, and your deal</span>
                </li>
              </ul>
              <button type="button" className={styles.whyCta} onClick={goSell}>
                Create a free listing <ArrowForwardIcon />
              </button>
            </div>
          </div>
        </section>

        <section className={styles.howSection} id="how-it-works">
          <div className={styles.howHeading}>
            <span className={styles.sectionKicker}>From hello to handoff</span>
            <h2>Three steps. No detours.</h2>
            <p>
              Create an account once, then use DriveNow whether you are buying or
              selling.
            </p>
          </div>
          <div className={styles.howGrid}>
            <div className={styles.howStep}>
              <div className={styles.howStepNumber}>01</div>
              <span className={styles.howStepIcon}>
                <PersonAddIcon />
              </span>
              <div>
                <h4>Join DriveNow</h4>
                <p>Create your account and verify your contact details.</p>
              </div>
            </div>
            <div className={styles.howStep}>
              <div className={styles.howStepNumber}>02</div>
              <span className={styles.howStepIcon}>
                <DirectionsCarIcon />
              </span>
              <div>
                <h4>Browse or list</h4>
                <p>
                  Search the marketplace or publish your car with the guided
                  listing flow.
                </p>
              </div>
            </div>
            <div className={styles.howStep}>
              <div className={styles.howStepNumber}>03</div>
              <span className={styles.howStepIcon}>
                <HandshakeIcon />
              </span>
              <div>
                <h4>Connect and close</h4>
                <p>
                  Message privately, meet safely, and finalize the deal directly.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className={styles.confidenceSection}>
          <div className={styles.confidenceGrid}>
            <div className={styles.confidenceLead}>
              <span className={styles.sectionKicker}>Confidence comes standard</span>
              <h2>A marketplace that keeps the important things clear.</h2>
              <p>
                DriveNow gives buyers and sellers the tools to start a
                conversation without putting personal details on display.
              </p>
              {isGuest && (
                <button type="button" onClick={openSignUp}>
                  Join the marketplace <ArrowForwardIcon />
                </button>
              )}
            </div>
            <div className={styles.confidenceCards}>
              <article className={styles.confidenceCard}>
                <SecurityIcon />
                <h3>Private until you decide</h3>
                <p>Your email is not posted publicly.</p>
              </article>
              <article className={styles.confidenceCard}>
                <ChatBubbleOutlineIcon />
                <h3>Conversation in context</h3>
                <p>Messages stay connected to the listing you are discussing.</p>
              </article>
              <article className={`${styles.confidenceCard} ${styles.confidenceCardWide}`}>
                <LocationOnIcon />
                <div>
                  <h3>Local deals, handled your way</h3>
                  <p>
                    Arrange inspections and test drives directly, with no
                    platform pressure.
                  </p>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className={styles.faqSection} id="faq">
          <div className={styles.faqHeading}>
            <span className={styles.sectionKicker}>Questions, answered</span>
            <h2>Frequently asked questions</h2>
            <p>Everything you need to know before creating your free account.</p>
          </div>
          <div className={styles.faqList}>
            {FAQS.map((f) => (
              <details key={f.q} className={styles.faqItem}>
                <summary>{f.q}</summary>
                <p className={styles.faqAnswer}>{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className={styles.ctaSection}>
          <div className={styles.ctaGlow} />
          <div className={styles.ctaContent}>
            <span>Ready when you are</span>
            <h2>Put yourself in the driver&apos;s seat.</h2>
            <p>
              Browse live listings now, or create a free account to message
              sellers and list your own car.
            </p>
            <div className={styles.ctaActions}>
              <button type="button" className={styles.ctaButton} onClick={goBrowse}>
                Browse listings <ArrowForwardIcon />
              </button>
              {isGuest && (
                <button
                  type="button"
                  className={styles.aboutSecondaryCtaLight}
                  onClick={openSignUp}
                >
                  Create your free account
                </button>
              )}
            </div>
          </div>
        </section>
      </main>

      <footer className={homeStyles.footer}>
        <div className={homeStyles.footerContent}>
          <div className={homeStyles.footerBrand}>
            <h3>Drive Now!</h3>
            <p>
              The trusted marketplace for buying and selling cars directly from
              private sellers.
            </p>
          </div>
          <div className={homeStyles.footerLinks}>
            <div className={homeStyles.footerColumn}>
              <h4>Marketplace</h4>
              <Link to="/">Browse Cars</Link>
              <Link to="/search">Search all listings</Link>
              {isGuest ? (
                <button
                  type="button"
                  className={homeStyles.footerLinkButton}
                  onClick={openSignUp}
                >
                  Sell Your Car
                </button>
              ) : (
                <Link to="/newlisting">Sell Your Car</Link>
              )}
              <Link to="/about">About Us</Link>
            </div>
            <div className={homeStyles.footerColumn}>
              <h4>Support</h4>
              <a href="#faq">FAQ</a>
              <a href="#how-it-works">How It Works</a>
              <span>Safety Tips</span>
            </div>
            <div className={homeStyles.footerColumn}>
              <h4>Legal</h4>
              <span>Terms of Service</span>
              <span>Privacy Policy</span>
            </div>
          </div>
        </div>
        <div className={homeStyles.footerBottom}>
          <p>© {new Date().getFullYear()} Drive Now! All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
